/* eslint-disable no-nested-ternary */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { finLogo, ResetPasswordIcon, FooterLogo } from '../../assets';
import { resetSchema } from './validations';
import {
  resetPasswordApi,
  resetPasswordCodeCheck,
} from '../../Services/authService';
import { CustomBox, LoginContainer } from '../login/Register';
import { ErrorText, Loader } from '../../Components';

export function ResetPassword() {
  const { search } = useLocation();
  const code = search.split('=')[1];
  const [showResetUI, setShowResetUI] = useState();
  const [showSuccess, setShowSuccess] = useState(false);
  const [passwordNotMatched, setPasswordNotMatched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const navigate = useNavigate();
  const theme = useTheme();
  const form = useForm({
    resolver: yupResolver(resetSchema),
    mode: 'all',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  useEffect(() => {
    const checkCodeValidity = async () => {
      if (code) {
        try {
          const res = await resetPasswordCodeCheck(code);
          setLoading(false);
          if (res?.data?.message === 'Reset code is valid') {
            setShowResetUI(true);
          } else {
            setShowResetUI(false);
          }
        } catch (error) {
          setLoading(false);
          setShowResetUI(false);
        }
      }
    };

    checkCodeValidity();
  }, [code]);

  const handleResetPassword = async (data) => {
    try {
      const res = await resetPasswordApi({
        newPassword: data['new-password'],
        confirmPassword: data['confirm-password'],
        code,
      });

      if (res?.status === 200) {
        setSnackbar({
          open: true,
          message:
            'Password Updated!\nPassword has been changed Successfully use your new password to login.',
          severity: 'success',
        });
        navigate('/vkyc/login');
        setShowSuccess(true);
      }
    } catch (error) {
      const { message } = error;
      setPasswordNotMatched(message);
    }
  };

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prevShowNewPassword) => !prevShowNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (newPassword !== e.target.value) {
      setPasswordNotMatched(true);
    } else {
      setPasswordNotMatched(false);
    }
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (confirmPassword !== e.target.value) {
      setPasswordNotMatched(true);
    } else {
      setPasswordNotMatched(false);
    }
  };

  return (
    <>
      <CustomBox
        sx={{
          backgroundColor: '#001F3F',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 2,
        }}
      >
        <LoginContainer
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            component="form"
            sx={{
              background: '#ffffff',
              width: '100%',
              maxWidth: '420px',
              padding: { xs: '24px', sm: '32px' },
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              textAlign: 'center',
            }}
            onSubmit={handleSubmit(handleResetPassword)}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src={finLogo}
                  sx={{
                    width: '120px',
                    height: 'auto',
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Reset Password
              </Typography>

              {loading ? (
                <Loader />
              ) : !showResetUI ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    component="img"
                    src={ResetPasswordIcon}
                    sx={{ width: '60px', height: '60px' }}
                  />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    The password reset link has expired.
                    <br />
                    Please request a new one.
                  </Typography>
                  <Button
                    sx={{ mt: 4 }}
                    variant="outlined"
                    onClick={() =>
                      navigate('/login', { state: { showForgotPassword: true } })
                    }
                    fullWidth
                  >
                    Resend Link
                  </Button>
                </Box>
              ) : (
                <>
                  {passwordNotMatched && (
                    <ErrorText>Passwords do not match</ErrorText>
                  )}

                  <Box sx={{ textAlign: 'left', mb: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: 500 }}
                    >
                      New Password
                    </Typography>
                    <TextField
                      fullWidth
                      id="new-password"
                      size="small"
                      type={showNewPassword ? 'text' : 'password'}
                      {...register('new-password')}
                      disabled={!showResetUI}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      InputProps={{
                        sx: { height: 40 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleNewPasswordVisibility}
                              size="small"
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {!!errors['new-password'] && (
                      <ErrorText>{errors['new-password'].message}</ErrorText>
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'left', mb: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: 500 }}
                    >
                      Confirm Password
                    </Typography>
                    <TextField
                      fullWidth
                      id="confirm-password"
                      size="small"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirm-password')}
                      disabled={!showResetUI}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      InputProps={{
                        sx: { height: 40 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleConfirmPasswordVisibility}
                              size="small"
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {!!errors['confirm-password'] && (
                      <ErrorText>{errors['confirm-password'].message}</ErrorText>
                    )}
                  </Box>
                  <Box sx={{ pt: 2 }}>
                    <Button
                      sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={!showResetUI || passwordNotMatched}
                    >
                      Submit
                    </Button>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 1,
                      }}
                    >
                      <Button
                        sx={{
                          fontSize: '0.9rem',
                          '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: 'transparent',
                          },
                        }}
                        onClick={() => navigate('/login')}
                      >
                        Back To Login
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Box component="img" src={FooterLogo} />
              <Typography
                ml={0.5}
                sx={{ fontSize: '0.9rem', color: '#757575' }}
              >
                <Link
                  to="https://www.habilelabs.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#757575' }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#1976d2';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#757575';
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  Powered by Habilelabs
                </Link>
              </Typography>
            </Box>
          </Box>
        </LoginContainer>
      </CustomBox>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
